package com.hyperether.goodjob.scenes.contacts.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.navigation.NavHostController
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.navigation.JobDetails
import com.hyperether.goodjob.scenes.components.JobHeader
import com.hyperether.goodjob.scenes.components.disableMouseDragScrollOnWeb
import com.hyperether.goodjob.scenes.components.verticalScrollbar
import com.hyperether.goodjob.util.DateTimeUtil
import com.hyperether.goodjob.util.StatusColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import kotlinx.datetime.LocalDate

@Composable
fun JobListGroupedByDate(
    isJobList: Boolean = false,
    sites: Map<String, Site>?,
    jobs: List<Map.Entry<Comparable<LocalDate>, List<Job>>>,
    navHostController: NavHostController,
    location: String = ""
) {
    val scrollState = rememberScrollState()
    Column(
        modifier = Modifier.fillMaxSize()
            .verticalScrollbar(scrollState)
            .verticalScroll(scrollState)
            .disableMouseDragScrollOnWeb()
    ) {
        jobs.forEach { (date, jobsForDate) ->
            val formattedDate = if (date is LocalDate) {
                DateTimeUtil.formatDateForDisplay(date)
            } else {
                date.toString()
            }
            // Display the header for this date
            if (jobsForDate.isNotEmpty()) {
                JobHeader(createdAt = formattedDate)

                // Display all jobs under the same date header
                jobsForDate.forEachIndexed { index, job ->
                    JobListRow(
                        navHostController = navHostController,
                        job = job,
                        isEvenRow = index % 2 == 0,
                        if (isJobList) sites?.get(job.id)?.addressObject?.address
                            ?: "" else location
                    )
                }
            }

            // Add spacing between groups
            Spacer(modifier = Modifier.height(16.toScaleDp()))
        }
    }
}


@Composable
fun JobListRow(
    navHostController: NavHostController,
    job: Job,
    isEvenRow: Boolean,
    location: String = ""
) {
    val backgroundColor = if (isEvenRow) Color(0xFFF9F9F9) else Color.White
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .height(48.toScaleDp())
            .padding(horizontal = 22.toScaleDp())
            .background(backgroundColor)
            .clip(MaterialTheme.shapes.medium)
            .pointerHoverIcon(PointerIcon.Hand),
        verticalArrangement = Arrangement.Center
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.padding(horizontal = 24.toScaleDp()).clickable {
                job.id?.let { navHostController.navigate(JobDetails(it)) }
            }
        ) {
            // Row Details
            Text(
                text = job.jobTitle ?: " ",
                color = Color(0xFF34618E),
                fontSize = 16.toScaleSp(),
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(0.9f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = job.contactSiteName ?: " ",
                color = Color(0xFF323232),
                fontSize = 14.toScaleSp(),
                modifier = Modifier.weight(0.9f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = location,
                color = Color(0xFF555555),
                fontSize = 14.toScaleSp(),
                modifier = Modifier.weight(1f),
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = job.start_at?.let { start ->
                    job.end_at?.let { end ->
                        "$start - $end"
                    }
                } ?: "",
                color = Color(0xFF555555),
                fontSize = 14.toScaleSp(),
                modifier = Modifier.weight(0.7f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = job.assignees?.employees?.mapNotNull { it?.getName() }
                    ?.joinToString(", ")
                    ?: "",
                color = Color(0xFF323232),
                fontSize = 14.toScaleSp(),
                modifier = Modifier.weight(0.7f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Column(
                modifier = Modifier.weight(0.5f)
            ) {
                Box(
                    modifier = Modifier
                        .size(16.toScaleDp())
                        .clip(CircleShape)
                        .align(Alignment.CenterHorizontally)
                        .background(StatusColor.getStatusColor(job.status))
                )
            }
        }
    }
}